import { useState } from 'react';
import { Link } from 'react-router';
import { UserMinus } from 'lucide-react';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  MilestoneChips,
  Skeleton,
  SkeletonText,
  Tooltip,
  TrafficLight,
  cn,
  formatPercent,
} from '@mgdlead/ui';

import { useWorkspace } from '../workspace/WorkspaceContext';
import { workspacePath } from '../workspace/navigation';
import { useRetentionSummary, type RetentionSummary } from '../../lib/api';
import { avisoDeAmostra, LEGENDA_DO_CARD, motivoSemTaxa, taxaTexto } from './retention-ui';

/**
 * O card "Evasão" do Dashboard (Curva de Evasão, ADR-0019).
 *
 * Responde "o tráfego está segurando gente?" numa frase que vem PRONTA do servidor, com chips
 * D0…D90 para trocar o marco. O saldo continua sendo o número de topo do dia; este card é a
 * manchete de qualidade do tráfego, em cadência de turma.
 */
export function EvasaoCard() {
  const { workspace } = useWorkspace();
  const summary = useRetentionSummary(workspace.id);

  if (summary.isError) {
    return (
      <ErrorState
        title="Não conseguimos montar a evasão"
        description={summary.error.message}
        onRetry={() => void summary.refetch()}
      />
    );
  }
  if (summary.isPending) {
    return (
      <Card>
        <CardHeader title="Evasão" />
        <SkeletonText lines={2} />
        <Skeleton className="mt-3 h-7 w-full" />
      </Card>
    );
  }
  return (
    <Conteudo
      data={summary.data}
      historico={workspacePath(workspace.slug, 'analytics/historico')}
    />
  );
}

function marcoInicial(data: RetentionSummary): number {
  if (data.manchete) return data.manchete.marco;
  const habilitado = data.marcos.find((n) => data.indisponivel[n] === null);
  return habilitado ?? 7;
}

function Conteudo({ data, historico }: { data: RetentionSummary; historico: string }) {
  const [marco, setMarco] = useState<number>(() => marcoInicial(data));
  const n = marco as RetentionSummary['marcos'][number];

  if (data.primeiraEntrada === null) {
    return (
      <Card>
        <CardHeader title="Evasão" />
        <EmptyState
          kind="no-data"
          icon={UserMinus}
          title="Ainda sem turma de entrada"
          description="Assim que alguém entrar, mostramos aqui quantos saem com o tempo."
          compact
        />
      </Card>
    );
  }

  const atual = data.atual[n];
  const anterior = data.anterior[n];
  const semaforo = data.semaforo[n];
  const referencia = data.referencia[n];
  /** G3: com meta do bloco (D1/D7), o semáforo é contra a meta, não a mediana. */
  const meta = data.meta[n];
  const amostra = avisoDeAmostra(atual);
  const motivo = motivoSemTaxa(atual) ?? data.indisponivel[n];

  return (
    <Card>
      <CardHeader
        title="Evasão"
        description="Quem entrou e saiu por conta própria, por turma de entrada."
        action={
          <Link
            to={historico}
            className="inline-flex min-h-11 items-center text-sm font-medium text-primary-text hover:underline md:min-h-0"
          >
            Ver por turma
          </Link>
        }
      />

      {data.manchete ? (
        <p className="text-base text-foreground">{data.manchete.frase}</p>
      ) : (
        <p className="text-sm text-muted-foreground">
          Nenhuma turma completou um marco ainda — ainda não deu tempo de medir.
        </p>
      )}

      <div className="mt-4">
        <MilestoneChips
          value={marco}
          onValueChange={setMarco}
          chips={data.marcos.map((m) => ({ value: m, disabledReason: data.indisponivel[m] }))}
        />
      </div>

      <div className="mt-3">
        <div className="flex flex-wrap items-center gap-2">
          {atual.valor === null ? (
            <Tooltip content={motivo ?? 'Sem dados.'}>
              <span tabIndex={0} className="nums text-2xl font-semibold text-muted-foreground">
                {taxaTexto(atual)}
              </span>
            </Tooltip>
          ) : (
            <span className="nums text-2xl font-semibold text-foreground">{taxaTexto(atual)}</span>
          )}
          <TrafficLight
            status={semaforo}
            hint={
              atual.valor === null
                ? undefined
                : meta !== null
                  ? `Meta: até ${taxaTexto({ ...atual, valor: meta })} · Agora: ${taxaTexto(atual)}`
                  : referencia === null
                    ? undefined
                    : `Sua mediana de 28 dias: ${taxaTexto({ ...atual, valor: referencia })} · Agora: ${taxaTexto(atual)}`
            }
          />
          <span className="nums text-xs text-muted-foreground">
            na última semana · semana anterior: {taxaTexto(anterior)}
          </span>
          {amostra ? (
            <Badge tone="warning" dot>
              {amostra}
            </Badge>
          ) : null}
        </div>
      </div>

      <TurmasDoMarco
        serie={data.serie[n]}
        marco={marco}
        linha={
          meta !== null
            ? { valor: meta, rotulo: `meta ${taxaTexto({ ...atual, valor: meta })}` }
            : referencia !== null
              ? {
                  valor: referencia,
                  rotulo: `seu normal ${taxaTexto({ ...atual, valor: referencia })}`,
                }
              : null
        }
      />

      <p className="mt-3 text-xs text-muted-foreground">{LEGENDA_DO_CARD}</p>
    </Card>
  );
}

/** "no mesmo dia" · "em até 3 dias". */
function prazo(marco: number): string {
  return marco === 0 ? 'no mesmo dia' : `em até ${marco} ${marco === 1 ? 'dia' : 'dias'}`;
}

const diaMes = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

/**
 * Uma coluna por TURMA (o dia em que as pessoas entraram), com quantos % delas já saíram no
 * marco escolhido (pedido do dono, 10/10: o minigráfico de 28 pontos, quase todo hachurado,
 * "não dá para entender"). Os dias antes da primeira turma não aparecem; dia sem turma no meio
 * é hachura, nunca 0%. A linha tracejada é a régua do semáforo (meta ou o seu normal).
 */
function TurmasDoMarco({
  serie,
  marco,
  linha,
}: {
  serie: RetentionSummary['serie'][keyof RetentionSummary['serie']];
  marco: number;
  linha: { valor: number; rotulo: string } | null;
}) {
  const primeira = serie.findIndex((p) => p.valor !== null);
  const pontos = primeira < 0 ? [] : serie.slice(primeira);
  const [escolhido, setEscolhido] = useState<string | null>(null);
  if (pontos.length === 0) return null;
  const ultimo = [...pontos].reverse().find((p) => p.valor !== null)!;
  const sel = pontos.find((p) => p.date === escolhido && p.valor !== null) ?? ultimo;
  const teto = Math.max(0.1, linha?.valor ?? 0, ...pontos.map((p) => p.valor ?? 0));
  const passo = pontos.length > 14 ? 7 : pontos.length > 7 ? 2 : 1;

  return (
    <div className="mt-5">
      <p className="text-sm font-medium text-foreground">Por dia de entrada</p>
      <p className="text-xs text-muted-foreground">
        Cada coluna é o grupo de pessoas que entrou naquele dia. A altura é quantos % delas saíram{' '}
        {prazo(marco)}. Toque numa coluna para ver o dia.
      </p>

      <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-[1fr_13rem]">
        <div className="min-w-0">
          <div
            role="group"
            aria-label={`Evasão ${prazo(marco)}, por dia de entrada`}
            className="relative flex h-28 items-end justify-around gap-1 border-b border-border"
          >
            {linha !== null ? (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 border-t border-dashed border-muted-foreground"
                style={{ bottom: `${(linha.valor / teto) * 100}%` }}
              >
                <span className="absolute -top-4 right-0 bg-card px-1 text-2xs text-muted-foreground">
                  {linha.rotulo}
                </span>
              </div>
            ) : null}
            {pontos.map((p) =>
              p.valor === null ? (
                <span
                  key={p.date}
                  title={`${diaMes(p.date)}: ninguém entrou`}
                  className="h-1/3 max-w-20 flex-1 rounded-t-sm bg-hatch opacity-50"
                />
              ) : (
                <button
                  key={p.date}
                  type="button"
                  onClick={() => setEscolhido(p.date)}
                  aria-pressed={sel.date === p.date}
                  aria-label={`Entraram em ${diaMes(p.date)}: ${formatPercent(p.valor, 0)} saíram ${prazo(marco)}`}
                  className={cn(
                    'max-w-20 flex-1 rounded-t-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    sel.date === p.date ? 'bg-primary' : 'bg-primary/35 hover:bg-primary/55',
                  )}
                  style={{ height: `${Math.max(2, (p.valor / teto) * 100)}%` }}
                />
              ),
            )}
          </div>
          <div
            aria-hidden="true"
            className="mt-1 flex justify-around gap-1 font-mono text-2xs text-subtle-foreground nums"
          >
            {pontos.map((p, i) => (
              <span key={p.date} className="max-w-20 flex-1 text-center">
                {i % passo === 0 || p.date === sel.date ? diaMes(p.date) : ''}
              </span>
            ))}
          </div>
        </div>

        <Card tone="sunken" size="sm" aria-live="polite">
          <p className="text-xs text-muted-foreground">Entraram em {diaMes(sel.date)}</p>
          <p className="nums mt-1 text-2xl font-semibold text-foreground">
            {sel.valor === null ? '—' : formatPercent(sel.valor, 0)}
          </p>
          <p className="text-xs text-muted-foreground">saíram {prazo(marco)}</p>
        </Card>
      </div>
    </div>
  );
}
